import { useState } from "react";
import { errorNotification } from "../../../../utils/notifications";
import { DIAS_SEMANA } from "../horariosClubesComponentConstants";
import { State, Props } from "../horariosClubesComponentTypes";

export const useHorariosClubes = (props: Props) => {
  const [state, setState] = useState<State>({
    configuraciones: [],
    selectedDia: null,
    modalHorario: false,
    horaInicio: "",
    horaFin: "",
    nuevaConfiguracion: {
      mantenimientoIds: [],
      clubIds: [],
      horarios: []
    },
    modalNuevaConfiguracion: false,
    currentConfiguracionId: null,
    modalEditarMantenimientos: false,
    modalEditarClubes: false,
    configuracionEditando: null,
    mantenimientosSeleccionados: [],
    clubesSeleccionados: [],
    modalConfirmarEliminar: false,
    configuracionAEliminar: null
  });

  const loadingStates = {
    searching: props.loadingStates.searching,
    saving: props.loadingStates.saving,
    updating: props.loadingStates.updating,
    deleting: props.loadingStates.deleting
  };

  const isAnyOperationInProgress = () => {
    return Object.values(loadingStates).some(state => state);
  };

  // === HELPER PARA ACTUALIZAR ESTADO ===
  const updateState = (updates: Partial<State>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  // === MÉTODOS DE TOGGLE ===
  const toggleModalHorario = () => {
    setState(prev => ({ ...prev, modalHorario: !prev.modalHorario }));
  };

  const toggleModalNuevaConfiguracion = () => {
    if (!state.modalNuevaConfiguracion) {
      setState(prev => ({
        ...prev,
        modalNuevaConfiguracion: true,
        mantenimientosSeleccionados: [],
        clubesSeleccionados: [],
        nuevaConfiguracion: {
          mantenimientoIds: [],
          clubIds: [],
          horarios: DIAS_SEMANA.map(dia => ({
            dia: dia.key,
            horaInicio: "08:00",
            horaFin: "18:00",
            activo: false
          }))
        }
      }));
    } else {
      setState(prev => ({ ...prev, modalNuevaConfiguracion: false }));
    }
  };

  // === MÉTODOS DE CONFIRMACIÓN ===
  const abrirModalConfirmarEliminar = (configuracionId: string) => {
    setState(prev => ({
      ...prev,
      modalConfirmarEliminar: true,
      configuracionAEliminar: configuracionId
    }));
  };

  const cerrarModalConfirmarEliminar = () => {
    setState(prev => ({
      ...prev,
      modalConfirmarEliminar: false,
      configuracionAEliminar: null
    }));
  };

  // === MÉTODOS DE HORARIOS ===
  const handleDiaClick = (dia: string, configuracionId: string) => {
    const configuracion = props.configuraciones.find(
      c => c.configuracionId.toString() === configuracionId
    );
    const horario = configuracion
      ? configuracion.horarios.find((h: any) => h.dia === dia)
      : undefined;

    setState(prev => ({
      ...prev,
      selectedDia: dia,
      currentConfiguracionId: configuracionId,
      horaInicio: horario ? horario.horaInicio : "08:00",
      horaFin: horario ? horario.horaFin : "18:00",
      modalHorario: true
    }));
  };

  const guardarHorario = async () => {
    const { selectedDia, horaInicio, horaFin, currentConfiguracionId } = state;

    if (!currentConfiguracionId || !selectedDia) return;

    const configuracion = props.configuraciones.find(
      c => c.configuracionId.toString() === currentConfiguracionId
    );
    if (!configuracion) return;

    const horariosActualizados = [...configuracion.horarios];
    const indexHorario = horariosActualizados.findIndex(
      (h: any) => h.dia === selectedDia
    );

    if (indexHorario >= 0) {
      horariosActualizados[indexHorario] = {
        dia: selectedDia,
        horaInicio,
        horaFin,
        activo: true
      };
    } else {
      horariosActualizados.push({
        dia: selectedDia,
        horaInicio,
        horaFin,
        activo: true
      });
    }

    const requestData = {
      mantenimientoIds: configuracion.mantenimientos.map((m: any) => m.id),
      clubIds: configuracion.clubes.map((c: any) => c.id),
      horarios: horariosActualizados
    };

    try {
      await props.actualizarConfiguracionesHorariosClubes(
        currentConfiguracionId,
        requestData
      );
      await props.getConfiguracionesHorariosClubes();

      setState(prev => ({
        ...prev,
        modalHorario: false,
        currentConfiguracionId: null
      }));
    } catch (error) {
      errorNotification("Error al actualizar el horario");
    }
  };

  // === MÉTODOS DE CONFIGURACIÓN ===
  const eliminarConfiguracion = async () => {
    const { configuracionAEliminar } = state;
    if (!configuracionAEliminar) return;

    try {
      await props.eliminarConfiguracionesHorariosClubes(configuracionAEliminar);
      await props.getConfiguracionesHorariosClubes();
    } catch (error) {
      errorNotification("Error al eliminar la configuración");
    } finally {
      cerrarModalConfirmarEliminar();
    }
  };

  const getClubsDisponibles = () => {
    /* const clubsUsados = props.configuraciones.reduce(
      (acc, config) => {
        return acc.concat(config.clubes.map((c: any) => c.id.toString()));
      },
      [] as string[]
    );
    return props.clubes.filter(
      (club: any) => !clubsUsados.includes(club.id.toString())
    ); */
    return props.clubes;
  };

  const validarHorarios = () => {
    const { nuevaConfiguracion } = state;
    const horariosActivos = nuevaConfiguracion.horarios.filter(h => h.activo);

    if (horariosActivos.length === 0) {
      return "Debe configurar al menos un día con horarios";
    }

    for (const horario of horariosActivos) {
      if (!horario.horaInicio || !horario.horaFin) {
        return "Todos los días activos deben tener hora de inicio y fin configuradas";
      }
      if (horario.horaInicio >= horario.horaFin) {
        return "La hora de inicio debe ser menor que la hora de fin";
      }
    }

    return null;
  };

  const seleccionarTodosMantenimientos = () => {
    const todosIds = props.mantenimientos.map((m: any) => m.id.toString());
    setState(prev => ({ ...prev, mantenimientosSeleccionados: todosIds }));
  };

  const seleccionarTodosClubes = () => {
    const todosIds = props.clubes.map((c: any) => c.id.toString());
    setState(prev => ({ ...prev, clubesSeleccionados: todosIds }));
  };

  const crearNuevaConfiguracion = async () => {
    const {
      mantenimientosSeleccionados,
      clubesSeleccionados,
      nuevaConfiguracion
    } = state;

    if (
      mantenimientosSeleccionados.length === 0 ||
      clubesSeleccionados.length === 0
    ) {
      errorNotification("Selecciona al menos un mantenimiento y un club");
      return;
    }

    const errorHorarios = validarHorarios();
    if (errorHorarios) {
      errorNotification(errorHorarios);
      return;
    }

    const requestData = {
      mantenimientoIds: mantenimientosSeleccionados.map(id => parseInt(id)),
      clubIds: clubesSeleccionados.map(id => parseInt(id)),
      horarios: nuevaConfiguracion.horarios.filter(h => h.activo)
    };

    try {
      await props.crearConfiguracionesHorariosClubes(requestData);
      await props.getConfiguracionesHorariosClubes();

      setState(prev => ({
        ...prev,
        modalNuevaConfiguracion: false,
        mantenimientosSeleccionados: [],
        clubesSeleccionados: [],
        nuevaConfiguracion: {
          mantenimientoIds: [],
          clubIds: [],
          horarios: DIAS_SEMANA.map(dia => ({
            dia: dia.key,
            horaInicio: "08:00",
            horaFin: "18:00",
            activo: false
          }))
        }
      }));
    } catch (error) {
      errorNotification("Error al crear la configuración");
    }
  };

  // === MÉTODOS DE EDICIÓN ===
  const abrirEditarMantenimientos = (configuracionId: string) => {
    const config = props.configuraciones.find(
      c => c.configuracionId.toString() === configuracionId
    );
    setState(prev => ({
      ...prev,
      modalEditarMantenimientos: true,
      configuracionEditando: configuracionId,
      mantenimientosSeleccionados: config
        ? config.mantenimientos.map((m: any) => m.id.toString())
        : []
    }));
  };

  const abrirEditarClubes = (configuracionId: string) => {
    const config = props.configuraciones.find(
      c => c.configuracionId.toString() === configuracionId
    );
    setState(prev => ({
      ...prev,
      modalEditarClubes: true,
      configuracionEditando: configuracionId,
      clubesSeleccionados: config
        ? config.clubes.map((c: any) => c.id.toString())
        : []
    }));
  };

  const guardarMantenimientos = async () => {
    const { configuracionEditando, mantenimientosSeleccionados } = state;

    if (!configuracionEditando || mantenimientosSeleccionados.length === 0) {
      errorNotification("Selecciona al menos un mantenimiento");
      return;
    }

    const configuracion = props.configuraciones.find(
      c => c.configuracionId.toString() === configuracionEditando
    );
    if (!configuracion) return;

    const requestData = {
      mantenimientoIds: mantenimientosSeleccionados.map(id => parseInt(id)),
      clubIds: configuracion.clubes.map((c: any) => c.id),
      horarios: configuracion.horarios
    };

    try {
      await props.actualizarConfiguracionesHorariosClubes(
        configuracionEditando,
        requestData
      );
      await props.getConfiguracionesHorariosClubes();

      setState(prev => ({
        ...prev,
        modalEditarMantenimientos: false,
        configuracionEditando: null,
        mantenimientosSeleccionados: []
      }));
    } catch (error) {
      errorNotification("Error al actualizar mantenimientos");
    }
  };

  const guardarClubes = async () => {
    const { configuracionEditando, clubesSeleccionados } = state;

    if (!configuracionEditando || clubesSeleccionados.length === 0) {
      errorNotification("Selecciona al menos un club");
      return;
    }

    /*     const otrasConfiguraciones = props.configuraciones.filter(
      c => c.configuracionId.toString() !== configuracionEditando
    );
    const clubesUsados = otrasConfiguraciones.reduce(
      (acc, config) => {
        return acc.concat(config.clubes.map((c: any) => c.id.toString()));
      },
      [] as string[]
    );

    const conflictos = clubesSeleccionados.filter(clubId =>
      clubesUsados.includes(clubId)
    );
    if (conflictos.length > 0) {
      errorNotification(
        "Algunos clubes ya están siendo utilizados por otras configuraciones"
      );
      return;
    } */

    const configuracion = props.configuraciones.find(
      c => c.configuracionId.toString() === configuracionEditando
    );
    if (!configuracion) return;

    const requestData = {
      mantenimientoIds: configuracion.mantenimientos.map((m: any) => m.id),
      clubIds: clubesSeleccionados.map(id => parseInt(id)),
      horarios: configuracion.horarios
    };

    try {
      await props.actualizarConfiguracionesHorariosClubes(
        configuracionEditando,
        requestData
      );
      await props.getConfiguracionesHorariosClubes();

      setState(prev => ({
        ...prev,
        modalEditarClubes: false,
        configuracionEditando: null,
        clubesSeleccionados: []
      }));
    } catch (error) {
      errorNotification("Error al actualizar clubes");
    }
  };

  // === RETORNAR ESTADO Y MÉTODOS ===
  return {
    state,
    updateState,
    toggleModalHorario,
    toggleModalNuevaConfiguracion,
    abrirModalConfirmarEliminar,
    cerrarModalConfirmarEliminar,
    handleDiaClick,
    guardarHorario,
    eliminarConfiguracion,
    getClubsDisponibles,
    validarHorarios,
    seleccionarTodosMantenimientos,
    crearNuevaConfiguracion,
    abrirEditarMantenimientos,
    abrirEditarClubes,
    guardarMantenimientos,
    guardarClubes,
    seleccionarTodosClubes,
    loadingStates,
    isAnyOperationInProgress
  };
};
